'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faRoute } from '@fortawesome/pro-solid-svg-icons';
import useRouteState from 'src/hooks/routes/useRouteState';
import useRouteActions from 'src/hooks/routes/useRouteActions';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { ROUTES_DRAWER_ID } from '../RoutesDrawer';

/**
 * RouteControls - A toolbar component for route creation
 *
 * Provides controls to start creating a new route and cancel route editing.
 * Positioned absolutely in the bottom-right corner of the map.
 *
 * @example
 * ```tsx
 * <RouteControls />
 * ```
 */
const RouteControls = () => {
  const { isCreating, isEditing } = useRouteState();
  const { startCreate, cancel } = useRouteActions();
  const { openDrawer } = useDrawer();

  const isRouteActive = isCreating || isEditing;

  const handleToggleRoute = () => {
    if (isRouteActive) {
      cancel();
    } else {
      // Expand the panel when starting to create a route
      openDrawer(ROUTES_DRAWER_ID);
      startCreate();
    }
  };

  return (
    <div className="z-40 inline-flex flex-col items-end gap-2">
      <div className="bg-white rounded-lg shadow-[0px_8px_8px_0px_rgba(0,0,0,0.08)] border border-neutral-200 inline-flex flex-col overflow-hidden">
        <div className="bg-white rounded-lg shadow-[0px_8px_8px_0px_rgba(0,0,0,0.08)] border border-neutral-200 inline-flex flex-col overflow-hidden">
          <button
            onClick={handleToggleRoute}
            aria-label={isRouteActive ? 'Cancel route' : 'Create route'}
            className={`font-semibold transition-all duration-200 select-none text-base
                     active:bg-neutral-200  p-4 flex items-center justify-center rounded-none
                      ${
                        isRouteActive
                          ? 'bg-sky-600 text-white hover:bg-sky-700'
                          : 'bg-neutral-50 text-neutral-700  hover:bg-neutral-100'
                      }
                    `}
            data-testid="route-create-button"
          >
            <Icon icon={faRoute} className="font-bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouteControls;
